import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Check, X, ShieldCheck } from "lucide-react";

const LOGO = "/smartfx-logo.png";

const PW_RULES = [
  { key: "len", label: "At least 6 characters", test: (p) => p.length >= 6 },
  { key: "upper", label: "One uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter (a-z)", test: (p) => /[a-z]/.test(p) },
  { key: "special", label: "One special character ($ ? @ ! ...)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function AcceptInvite() {
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let on = true;
    (async () => {
      if (!token) { setError("This invitation link is invalid."); setChecking(false); return; }
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        if (on) setInvite(data);
      } catch (e) {
        if (on) setError(formatApiError(e.response?.data?.detail));
      } finally {
        if (on) setChecking(false);
      }
    })();
    return () => { on = false; };
  }, [token]);

  const pwOk = PW_RULES.every((r) => r.test(password));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-zA-Z]{3,20}$/.test(username)) { toast.error("Username: letters only, 3-20 characters"); return; }
    if (!pwOk) { toast.error("Password does not meet the requirements"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await acceptInvite({ token, username: username.trim().toLowerCase(), phone: phone.trim(), password });
      toast.success("Account activated — welcome to Smart FX!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#04060d] flex items-center justify-center px-5 py-10" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO} alt="Smart FX" className="h-12 w-auto mb-2" />
          <div className="font-heading text-xl font-bold text-white">Set up your account</div>
        </div>

        {checking ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1C8CFF]" /></div>
        ) : error ? (
          <div className="rounded-2xl border border-[#ff5b5b]/40 bg-[#ff5b5b]/10 p-6 text-center" data-testid="accept-invite-error">
            <X className="h-8 w-8 text-[#ff5b5b] mx-auto mb-2" />
            <p className="text-[#ffb4b4] text-sm">{error}</p>
            <Button onClick={() => navigate("/login")} className="mt-4 rounded-full" data-testid="accept-invite-to-login">Go to Login</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl border border-[#14233d] bg-[#0A1120] p-6 space-y-4">
            <div className="flex items-center gap-2 text-[#8fb4e6] text-sm">
              <ShieldCheck className="h-4 w-4 text-[#22e07b]" />
              Invitation for <b className="text-white">{invite?.email}</b>
            </div>
            <div>
              <Label className="text-xs text-[#5f7ba6]">Username (letters only)</Label>
              <Input value={username} onChange={(e) => setUsername(e.target.value)} data-testid="invite-username"
                placeholder="davidnapolleon" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" />
            </div>
            <div>
              <Label className="text-xs text-[#5f7ba6]">Phone number</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="invite-phone"
                placeholder="+509 3700 0000" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" />
            </div>
            <div>
              <Label className="text-xs text-[#5f7ba6]">Password</Label>
              <div className="relative mt-1">
                <Input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="invite-password" placeholder="••••••••" className="bg-[#060c18] border-[#14233d] rounded-xl h-11 pr-10" />
                <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5f7ba6]">
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-1">
                {PW_RULES.map((r) => {
                  const ok = r.test(password);
                  return (
                    <div key={r.key} className={`flex items-center gap-1.5 text-[11px] ${ok ? "text-[#22e07b]" : "text-[#5f7ba6]"}`}>
                      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {r.label}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#5f7ba6]">Confirm password</Label>
              <Input type={showPass ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="invite-confirm" placeholder="••••••••" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-11" />
            </div>
            <Button type="submit" disabled={busy} data-testid="invite-submit-button"
              className="w-full h-12 rounded-full text-white font-semibold hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Activate my account"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
